import { useAtomValue } from "jotai";
import { canv } from "@/canv/global";
import { elementBase } from "@/elementBase/elementBase";
import type { Group } from "@/group";
import { LockedIcon, UnlockedIcon } from "@/icons/widget-menu";
import { widgetMenuAvailableOptions } from "@/interface/AppStore";
import { selectionManager } from "@/tools/cursorTool";
import type { MenuOptionResult, SetActiveSubMenu } from "../../types";
import { lockElementAndDescendants } from "../lockElementAndDescendants";

interface UseLockButtonProps {
    setActiveSubMenu: SetActiveSubMenu;
}

export function useLockButton({ setActiveSubMenu }: UseLockButtonProps): MenuOptionResult {
    useAtomValue(widgetMenuAvailableOptions);

    const isLocked =
        canv.canvasController.selected.length !== 0 && canv.canvasController.selected[0].isLocked;

    const handleClick = () => {
        setActiveSubMenu(null);

        const selected = canv.canvasController.selected;
        if (!selected.length) {
            return;
        }

        canv.eventManager.openRecord();
        if (isLocked) {
            // unlock selected
            // выбрана заблокированная группа, мы ее хотим разблокировать
            // мы тут ориентируемся на то, что выбрана может быть только одна залоченая группа
            // и не может быть 2 и более выделенных групп, которые все залочены или частично залочены
            // (мы можем выделить мышкой одну залоченую группу, но не более)
            if (selected.length !== 1) {
                console.log(
                    "такого быть не должно - выделено больше 1 группы, когда одна из них залочена",
                );
                return;
            }
            const selectedGroup = selected[0];

            lockElementAndDescendants(selectedGroup, false);

            if (selectedGroup.parent) {
                // если мы выбрали элемент внутри группы и хотим его разлочить - нужно вначале разгруппировать
                // при выделении залоченой группы у нас выделяется группа и у нее есть parent - lock группа
                canv.canvasController.TryUngroup(selectedGroup.parent);
            } else if (selectedGroup.onlyChild() instanceof elementBase) {
                selectedGroup.fixState("lock");
                selectedGroup.isLocked = false;
                selectedGroup.saveState();

                selectionManager.setSelected([selectedGroup]);
            } else {
                const childGroups = [...selectedGroup.children];

                for (let i = 0; i < childGroups.length; i++) {
                    childGroups[i].fixState("parentUID");
                    childGroups[i].setParent(null);
                    childGroups[i].saveState();
                }

                selectedGroup.children = [];

                canv.eventManager.createBuildEvent("delete", selectedGroup);

                canv.canvasController.groups.delete(selectedGroup.UID);
                selectionManager.setSelected(childGroups as Group[]);
            }
        } else {
            // lock selected
            // выбраны не заблокированные группы/группа, мы хотим ее заблокировать
            // если выделена одна или несколько групп - создаем новую группу и лочим ее
            const [newGroup] = canv.canvasController.TryGroup(
                selected,
                true,
                selected.length === 1 ? selected[0].indexByParent : null,
            );
            lockElementAndDescendants(newGroup, true);
            selectionManager.setSelected([newGroup]);
        }
        canv.eventManager.closeRecord();
    };

    const content = isLocked ? <LockedIcon /> : <UnlockedIcon />;
    const tippyContent = isLocked ? "Разблокировать" : "Заблокировать";

    return {
        content,
        handleClick,
        tippyContent,
    };
}
